import type { TimeZone } from '@shared/utils/date';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

export interface TimeZoneContextValue {
  /**
   * IANA timezone code (e.g., "America/New_York", "Europe/Stockholm")
   * If undefined, uses the browser's local timezone
   */
  timeZone?: TimeZone;
}

const TimeZoneContext = createContext<TimeZoneContextValue | undefined>(
  undefined
);

export const useTimeZone = () => {
  return useContext(TimeZoneContext)?.timeZone;
};

export interface TimeZoneProviderProps {
  /**
   * IANA timezone code
   */
  timeZone?: TimeZone;
  children: ReactNode;
}

/**
 * Provider component that makes timezone information available to all child components.
 * For example, use this to wrap fleet domain components that need equipment-specific timezone context.
 *
 * @example
 * ```tsx
 * <TimeZoneProvider timeZone={equipment.timeZone?.ianaCode}>
 *   <Dashboard />
 * </TimeZoneProvider>
 * ```
 */
export const TimeZoneProvider = ({
  timeZone,
  children,
}: TimeZoneProviderProps) => {
  const value = useMemo<TimeZoneContextValue>(() => {
    return { timeZone };
  }, [timeZone]);

  return (
    <TimeZoneContext.Provider value={value}>
      {children}
    </TimeZoneContext.Provider>
  );
};
